import { createContext, useContext, useEffect, useMemo } from 'react'

export interface TrailItem {
  /** Already-translated label. Pages own their own wording. */
  label: string
  /** Omit on the last item — the current page is not a link to itself. */
  to?: string
}

export interface TrailApi {
  publish: (owner: object, items: TrailItem[] | null) => void
  release: (owner: object) => void
}
export interface TrailState extends TrailApi {
  trail: TrailItem[]
}

/** Provided by `PageTrailProvider` (see providers/PageTrailProvider.tsx). */
export const PageTrailContext = createContext<TrailState | null>(null)

/** Read the current trail. Empty outside the app shell. */
export function usePageTrailValue(): TrailItem[] {
  return useContext(PageTrailContext)?.trail ?? []
}

/**
 * Publish this page's position in the hierarchy so the retained top bar can
 * show depth — round 18, frame `18b`: «baren viser hele veien inn og hvilket
 * arrangement tilbudet hører til».
 *
 * The shell cannot compute that itself: `/event/:id/offer/edit` knows only a
 * UUID, and the name it must show belongs to a record the page fetches. So the
 * page publishes and the shell renders.
 *
 * Pass `null` while the record is still loading — the bar falls back to the
 * route's parent link rather than rendering an empty 56px strip.
 *
 * Call sites pass array literals, so the value is compared by content, not by
 * reference. `owner` is a per-instance identity, not a ref, because release has
 * to be safe: with `AnimatedOutlet` the outgoing page can still be mounted
 * while the incoming one runs its effects, so an unconditional clear on unmount
 * would wipe the new page's trail on the way out.
 */
export function usePageTrail(items: TrailItem[] | null): void {
  const ctx = useContext(PageTrailContext)
  const owner = useMemo(() => ({}), [])
  const publish = ctx?.publish
  const release = ctx?.release
  const key = items && items.length > 0 ? JSON.stringify(items) : ''

  useEffect(() => {
    if (!publish || !release) return
    publish(owner, key ? (JSON.parse(key) as TrailItem[]) : null)
    return () => release(owner)
  }, [key, owner, publish, release])
}
